import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Calendar, Clock } from 'lucide-react';
import { TIME_SLOTS, DURATIONS } from './constants';

export default function BookingSlotFields({
  locations, locationId, setLocationId,
  bookingDate, setBookingDate, todayISO,
  // Optional lower bound for the date picker. Manual mode may pass a
  // 30-days-ago floor to allow back-dating; Recurring + Block Court do
  // not pass this and fall back to today. (June 2026)
  minDate,
  duration, setDuration,
  timeSlot, setTimeSlot,
}) {
  const dateMin = minDate || todayISO();
  const isBackdated = bookingDate && bookingDate < todayISO();
  const dayOfWeekLabel = bookingDate
    ? new Date(`${bookingDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' })
    : '';
  return (
    <>
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Location
            </Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger data-testid="loc-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Date{dayOfWeekLabel && (
                <span data-testid="booking-date-weekday" className="text-emerald-700">: {dayOfWeekLabel}</span>
              )}
            </Label>
            <Input
              type="date"
              value={bookingDate}
              min={dateMin}
              onChange={(e) => setBookingDate(e.target.value)}
              data-testid="booking-date"
            />
            {isBackdated && (
              <p
                className="text-[11px] text-amber-700 mt-1 italic"
                data-testid="backdated-hint"
              >
                ⚠ Back-dating: this booking is being logged after the play date.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Duration
            </Label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
              <SelectTrigger data-testid="duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} hour{d > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Start Time</Label>
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {TIME_SLOTS.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => setTimeSlot(slot)}
              data-testid={`slot-${slot.split('-')[0]}`}
              className={`text-xs font-semibold py-2 px-2 rounded-lg border transition-colors ${
                timeSlot === slot
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {slot.split('-')[0]}
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}
